import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Check,
  X,
  Users,
} from 'lucide-react';
import { api, Plan, ApiError, Feature } from '@/lib/api';

const formatPrice = (price: number, billingCycle: string) => {
  if (price === 0) return 'Sob consulta';
  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(price);
  return `${formatted}/${billingCycle === 'monthly' ? 'mes' : 'ano'}`;
};

export default function PlansPage() {
  const [search, setSearch] = React.useState('');
  const [includeInactive, setIncludeInactive] = React.useState(false);
  const [plans, setPlans] = React.useState<Plan[]>([]);
  const [features, setFeatures] = React.useState<Feature[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState<string | null>(null);
  const [tenantsCount, setTenantsCount] = React.useState<Record<string, number>>({});

  // Cria um map de key -> name para exibicao das features
  const featureLabels = React.useMemo(() => {
    const labels: Record<string, string> = {};
    features.forEach((f) => {
      labels[f.key] = f.name;
    });
    return labels;
  }, [features]);

  // Cria um map de key -> displayOrder para ordenacao
  const featureOrder = React.useMemo(() => {
    const order: Record<string, number> = {};
    features.forEach((f) => {
      order[f.key] = f.displayOrder;
    });
    return order;
  }, [features]);

  // Funcao para ordenar as features de um plano pelo displayOrder
  const getSortedPlanFeatures = React.useCallback(
    (planFeatures: Record<string, boolean>) => {
      return Object.entries(planFeatures).sort(([keyA], [keyB]) => {
        const orderA = featureOrder[keyA] ?? 999;
        const orderB = featureOrder[keyB] ?? 999;
        return orderA - orderB;
      });
    },
    [featureOrder]
  );

  const loadPlans = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [plansData, featuresData] = await Promise.all([
        api.getPlans(includeInactive),
        api.getFeatures(),
      ]);
      setPlans(plansData);
      setFeatures(featuresData);

      // Load tenants count for each plan
      const counts: Record<string, number> = {};
      await Promise.all(
        plansData.map(async (plan) => {
          try {
            const result = await api.getPlanTenantsCount(plan.id);
            counts[plan.id] = result.count;
          } catch {
            counts[plan.id] = 0;
          }
        })
      );
      setTenantsCount(counts);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erro ao carregar planos');
    } finally {
      setIsLoading(false);
    }
  }, [includeInactive]);

  React.useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleDelete = async (id: string, name: string) => {
    if (
      !confirm(
        `Tem certeza que deseja excluir o plano "${name}"? Esta acao nao pode ser desfeita.`
      )
    ) {
      return;
    }

    try {
      setIsDeleting(id);
      await api.deletePlan(id);
      setPlans((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      const apiError = err as ApiError;
      alert(apiError.message || 'Erro ao excluir plano');
    } finally {
      setIsDeleting(null);
    }
  };

  const filteredPlans = plans.filter(
    (plan) =>
      plan.name.toLowerCase().includes(search.toLowerCase()) ||
      plan.slug.toLowerCase().includes(search.toLowerCase())
  );

  const limitLabels: Record<string, string> = {
    maxUsers: 'Max Usuarios',
    maxStorageGB: 'Storage (GB)',
    maxAdmins: 'Max Admins',
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Planos</h1>
          <p className="text-gray-500 mt-1">
            Gerencie os planos disponiveis na plataforma
          </p>
        </div>
        <Link
          to="/plans/new"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#0057FF] to-[#7C3AED] text-white font-medium text-sm hover:opacity-90 transition-opacity shadow-lg shadow-[#0057FF]/25"
        >
          <Plus className="h-5 w-5" />
          Novo Plano
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar plano..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white px-12 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[#0057FF] focus:ring-[#0057FF]"
            />
            <span className="text-sm text-gray-600">Incluir inativos</span>
          </label>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-[#0057FF]" />
            <p className="text-gray-500">Carregando planos...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="bg-red-50 rounded-2xl border border-red-100 p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">
                Erro ao carregar planos
              </h3>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
            <button
              onClick={loadPlans}
              className="px-4 py-2 rounded-xl bg-red-100 text-red-700 text-sm font-medium hover:bg-red-200 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      {!isLoading && !error && (
        <>
          {filteredPlans.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12">
              <div className="flex flex-col items-center gap-4">
                <div className="h-16 w-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                  <Search className="h-8 w-8 text-gray-400" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-gray-900">
                    Nenhum plano encontrado
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {search
                      ? 'Tente buscar com outros termos'
                      : 'Comece criando seu primeiro plano'}
                  </p>
                </div>
                {!search && (
                  <Link
                    to="/plans/new"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0057FF] text-white text-sm font-medium hover:bg-[#0057FF]/90 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Novo Plano
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all hover:shadow-md ${
                    plan.isActive ? 'border-gray-100' : 'border-gray-200 opacity-60'
                  }`}
                >
                  {/* Plan Header */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {plan.name}
                        </h3>
                        <code className="text-xs text-gray-400 font-mono">
                          {plan.slug}
                        </code>
                      </div>
                      <div
                        className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          plan.isActive
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {plan.isActive ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        {plan.isActive ? 'Ativo' : 'Inativo'}
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-[#0057FF] mt-3">
                      {formatPrice(plan.price, plan.billingCycle)}
                    </p>
                    {plan.description && (
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                        {plan.description}
                      </p>
                    )}
                  </div>

                  {/* Tenants Count */}
                  <div className="px-6 py-3 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">
                        {tenantsCount[plan.id] ?? '...'} cliente(s)
                      </span>
                    </div>
                  </div>

                  {/* Recursos */}
                  <div className="p-6 space-y-4">
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Recursos
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {getSortedPlanFeatures(plan.features).map(([key, enabled]) => (
                          <span
                            key={key}
                            className={`px-2 py-1 rounded-md text-xs ${
                              enabled
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-gray-100 text-gray-400 line-through'
                            }`}
                          >
                            {featureLabels[key] || key}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Limites
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {Object.entries(plan.limits).map(([key, value]) => (
                          <div key={key} className="text-center">
                            <p className="text-lg font-bold text-gray-900">
                              {value === -1 ? '∞' : value}
                            </p>
                            <p className="text-xs text-gray-500">
                              {limitLabels[key] || key}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        Ordem: {plan.displayOrder}
                      </span>
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/plans/${plan.id}/edit`}
                          className="p-2 rounded-lg text-gray-400 hover:text-[#0057FF] hover:bg-[#0057FF]/10 transition-colors"
                        >
                          <Pencil className="h-5 w-5" />
                        </Link>
                        <button
                          onClick={() => handleDelete(plan.id, plan.name)}
                          disabled={
                            isDeleting === plan.id ||
                            (tenantsCount[plan.id] ?? 0) > 0
                          }
                          className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={
                            (tenantsCount[plan.id] ?? 0) > 0
                              ? 'Nao e possivel excluir plano com clientes'
                              : 'Excluir plano'
                          }
                        >
                          {isDeleting === plan.id ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <Trash2 className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary */}
          {filteredPlans.length > 0 && (
            <div className="text-sm text-gray-500 text-center">
              Mostrando{' '}
              <span className="font-medium text-gray-900">
                {filteredPlans.length}
              </span>{' '}
              de{' '}
              <span className="font-medium text-gray-900">{plans.length}</span>{' '}
              planos
            </div>
          )}
        </>
      )}
    </div>
  );
}
